import type { Action } from "./dataTypes/Action";
import type { Content } from "./dataTypes/Content";
import type { ContentType } from "./dataTypes/ContentType";

/**
 * In-memory simulation of a relink operation on the graph inputs, so the modal
 * can preview the *real* resulting graph (via buildGraph) before persisting.
 *
 * It faithfully materialises a "new button" (mirroring twoActionGirlsOneContentCup):
 * a synthetic content + content-type + the 3 actions (onSceneLoad to show the
 * button, onFilled to activate the target, onFilled to leave the host scene).
 * This way buildGraph lays the button out as a real sibling interaction — adding
 * a path WITHOUT removing the host scene's existing links.
 *
 * `greenEdges` are the edges the change introduces (host→button, button→target,
 * or a direct trigger→target / redirected edge) for explicit highlighting, and
 * `newButtonContentIds` are the created button nodes to emphasise.
 */

// onSceneLoad (content_scene) / onFilled (content_button_1) event-type ids.
const EVENT_ON_SCENE_LOAD = 24;
const EVENT_ON_FILLED = 8;
const BUTTON_CONTENT_TYPE = 5; // content_button_1

export type RelinkSimEdge =
  | {
      kind: "direct";
      affectContentId: number;
      targetContentId: number;
      deactivateTargetContentId: number;
      eventTypeId: number;
    }
  | { kind: "button"; hostSceneContentId: number; targetContentId: number };

export type RelinkSimOp = {
  redirects: { activateActionId: number; toContentId: number }[];
  edges: RelinkSimEdge[];
  // activate actions to drop (with their paired deactivate) — used by the "move"
  // bridge to remove the scene's old outgoing edge that would otherwise loop.
  removals?: { activateActionId: number }[];
};

export type RelinkSimResult = {
  contents: Content[];
  contentTypes: ContentType[];
  actions: Action[];
  greenEdges: { fromContentId: number; toContentId: number }[];
  newButtonContentIds: number[];
};

// Negative, ever-decreasing fake ids — never collide with real (positive) ids.
let fakeId = -1;
const nextId = () => fakeId--;

export const simulateRelink = (
  contents: Content[],
  contentTypes: ContentType[],
  actions: Action[],
  op: RelinkSimOp,
  projectId: number,
): RelinkSimResult => {
  const outContents = [...contents];
  const outContentTypes = [...contentTypes];
  let outActions = actions.map((a) => ({ ...a }));
  const greenEdges: { fromContentId: number; toContentId: number }[] = [];
  const newButtonContentIds: number[] = [];

  // Removals: drop an activate action AND its paired deactivate (same trigger +
  // event_type). Used by the move/bridge to delete the old outgoing edge.
  for (const r of op.removals ?? []) {
    const act = actions.find((a) => a.id === r.activateActionId);
    const removeIds = new Set<number>([r.activateActionId]);
    if (act) {
      for (const a of actions) {
        if (
          !a.is_activate &&
          a.affect_content === act.affect_content &&
          a.event_type === act.event_type
        ) {
          removeIds.add(a.id);
        }
      }
    }
    outActions = outActions.filter((a) => !removeIds.has(a.id));
  }

  // Redirects: re-point an existing activate action to a new target.
  for (const r of op.redirects) {
    const original = actions.find((a) => a.id === r.activateActionId);
    outActions = outActions.map((a) =>
      a.id === r.activateActionId ? { ...a, affected_content: r.toContentId } : a,
    );
    if (original) {
      greenEdges.push({ fromContentId: original.affect_content, toContentId: r.toContentId });
    }
  }

  // New edges.
  for (const e of op.edges) {
    if (e.kind === "direct") {
      outActions.push(
        {
          id: nextId(),
          is_activate: true,
          event_type: e.eventTypeId,
          affect_content: e.affectContentId,
          affected_content: e.targetContentId,
        },
        {
          id: nextId(),
          is_activate: false,
          event_type: e.eventTypeId,
          affect_content: e.affectContentId,
          affected_content: e.deactivateTargetContentId,
        },
      );
      greenEdges.push({ fromContentId: e.affectContentId, toContentId: e.targetContentId });
      continue;
    }

    // Materialise a real "Continuer" button under the host scene.
    const buttonContentId = nextId();
    newButtonContentIds.push(buttonContentId);

    outContents.push({
      id: buttonContentId,
      name: "Continuer",
      position_x: 0,
      position_y: 0,
      position_z: 0,
      rotation_x: 0,
      rotation_y: 0,
      rotation_z: 0,
      scale_x: 1,
      scale_y: 1,
      scale_z: 1,
      project: projectId,
      content_type: BUTTON_CONTENT_TYPE,
      parent: e.hostSceneContentId,
    });

    outContentTypes.push({
      modelName: "content-button-1",
      object: { id: nextId(), content: buttonContentId, name: "Continuer" },
    } as unknown as ContentType);

    outActions.push(
      // onSceneLoad: the host scene makes the button appear
      {
        id: nextId(),
        is_activate: true,
        event_type: EVENT_ON_SCENE_LOAD,
        affect_content: e.hostSceneContentId,
        affected_content: buttonContentId,
      },
      // onFilled: clicking the button activates the target
      {
        id: nextId(),
        is_activate: true,
        event_type: EVENT_ON_FILLED,
        affect_content: buttonContentId,
        affected_content: e.targetContentId,
      },
      // onFilled: clicking the button leaves the host scene
      {
        id: nextId(),
        is_activate: false,
        event_type: EVENT_ON_FILLED,
        affect_content: buttonContentId,
        affected_content: e.hostSceneContentId,
      },
    );

    greenEdges.push(
      { fromContentId: e.hostSceneContentId, toContentId: buttonContentId },
      { fromContentId: buttonContentId, toContentId: e.targetContentId },
    );
  }

  return {
    contents: outContents,
    contentTypes: outContentTypes,
    actions: outActions,
    greenEdges,
    newButtonContentIds,
  };
};
